import store from 'packages/store';
import { getPortalDomain } from 'packages/store/platform/platform.selectors';
import { parseUrl } from 'packages/helpers/urlUtils';

/**
 * TEMPORARY: remove this entire helper (and its call in src/index.tsx) once
 * Studio no longer runs inside an iframe. It only exists to work around the
 * cross-origin iframe setup and should be deleted with the iframe removal.
 *
 * TODO(iframe-removal): delete this file and its usage in src/index.tsx.
 *
 * Some third-party Bynder libraries hardcode their backend calls as
 * same-origin *relative* URLs (e.g. `@bynder/localization`, which is used
 * inside `@bynder/compact-view`'s "dat-frontend" transformations module,
 * fetches `/v7/localization/keys/`, `/v7/portal/.../custom-translations` and
 * `/v7/idp/access-tokens/userless-by-session`).
 *
 * Because Studio runs inside an iframe served from its own origin, those
 * relative requests resolve against the app origin (e.g. `localhost:8080` /
 * the Studio host) instead of the customer's Bynder portal, and fail. The
 * portal domain is dynamic (per customer) and only known at runtime, so it
 * cannot be fixed with a static webpack/nginx proxy or a library prop.
 *
 * This installs a `window.fetch` wrapper that rewrites every same-origin
 * *relative* `/v7/*` request to the absolute portal URL from Redux. The app's
 * own traffic is unaffected: it either goes through `/gateway/*` or uses
 * *absolute* portal URLs (e.g. `${portal}/v7/api/graphql`, `${portal}/v7/studio`),
 * neither of which is a same-origin relative `/v7/*` path.
 *
 * NOTE: rewritten calls become cross-origin, so the portal must allow CORS from
 * the Studio origin (`Access-Control-Allow-Origin` + `Access-Control-Allow-Credentials`).
 */
const shouldRewrite = (pathname: string): boolean => pathname.startsWith('/v7/');

const getRequestUrl = (input: RequestInfo | URL): string => {
    if (typeof input === 'string') {
        return input;
    }

    if (input instanceof URL) {
        return input.href;
    }

    return input.url;
};

let installed = false;

const installPortalFetchRewrite = (): void => {
    if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') {
        return;
    }

    // On localhost the webpack dev-server proxy (see webpack.devserver.mjs)
    // forwards these relative paths to the portal same-origin, avoiding CORS.
    // Leaving the requests relative there lets that proxy handle them.
    if (window.location.hostname === 'localhost') {
        return;
    }

    installed = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        try {
            const portalDomain = getPortalDomain(store.getState());

            if (portalDomain) {
                const requestUrl = getRequestUrl(input);
                const resolved = new URL(requestUrl, window.location.origin);
                const portalOrigin = new URL(parseUrl(portalDomain)).origin;

                const isSameOriginRelative = resolved.origin === window.location.origin;
                const portalIsElsewhere = portalOrigin !== window.location.origin;

                if (isSameOriginRelative && portalIsElsewhere && shouldRewrite(resolved.pathname)) {
                    const rewritten = `${portalOrigin}${resolved.pathname}${resolved.search}`;

                    // Ensure portal session cookies flow on the cross-origin request.
                    if (typeof input === 'string' || input instanceof URL) {
                        return originalFetch(rewritten, { credentials: 'include', ...init });
                    }

                    return originalFetch(new Request(rewritten, input), { credentials: 'include' });
                }
            }
        } catch {
            // On any parsing error, fall through to the original fetch untouched.
        }

        return originalFetch(input as RequestInfo, init);
    };
};

export default installPortalFetchRewrite;
